import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCustom, useCustomMutation, useInvalidate } from '@refinedev/core';
import { Languages, Save, Mic, AlertTriangle, Check, Volume2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';
import type {
  TranslationViewerMode,
  TranslationVoiceMode,
  TranslationSegmentPolicy,
  TranslationOriginalMix,
  TranslationStartPolicy,
  TranslationSessionStatus,
} from '@masters-club/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { FieldHint } from '@/components/ui/field-hint';
import { Switch } from '@/components/ui/switch';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { VoiceLibraryPanel } from './VoiceLibraryPanel';

/**
 * Таб «Перевод в реальном времени» на странице трансляции.
 *
 * Языки перевода берутся из справочника языков и сохраняются в собственное
 * поле настроек. Это НЕ то же самое, что «языки трансляции» на вкладке
 * доступа: там список определяет, кто вообще видит эфир, и запись туда
 * языка перевода скрыла бы трансляцию от остальных зрителей.
 */

const apiBase = resolveApiUrl().replace(/\/$/, '');

interface VoiceProfile {
  id: number;
  name: string;
  kind: string;
  archivedAt: string | null;
}

interface TranslationConfig {
  livestreamId: number;
  enabled: boolean;
  sourceLanguage: string;
  sourceLanguageAuto: boolean;
  targetLanguages: string[];
  defaultViewerMode: TranslationViewerMode;
  defaultOriginalVolume: number;
  defaultTranslationVolume: number;
  policy: TranslationSegmentPolicy;
  voiceMode: TranslationVoiceMode;
  defaultVoiceProfileId: number | null;
  neutralVoiceProfileId: number | null;
  originalUnderDub: TranslationOriginalMix;
  captionsEnabled: boolean;
  dubEnabled: boolean;
  /** Показывать субтитры оригинала одновременно с переводом. */
  dualCaptionsEnabled: boolean;
  startPolicy: TranslationStartPolicy;
  maxConcurrentLanes: number;
  diarizationEnabled: boolean;
  availableLanguages: string[];
  providerConfigured: boolean;
  /** Модель перевода. null → бэкенд подставит модель по умолчанию. */
  mtModelId: string | null;
  /** Модель распознавания речи. null → дефолт бэкенда. */
  sttModelId: string | null;
  /** Модель озвучки. null → дефолт бэкенда. */
  ttsModelId: string | null;
}

/** Ответ ручки проверки ключа провайдера. */
interface ProviderTestResult {
  ok: boolean;
  error?: string;
}

/** Ответ `getSessionStatus`: текущий статус конвейера перевода на эфире. */
interface TranslationSessionInfo {
  status: TranslationSessionStatus;
  session: string | null;
}

/**
 * Человекочитаемый текст ошибки сохранения.
 *
 * Валидатор отдаёт список замечаний в виде JSON-строки, и она попадала в
 * уведомление как есть — администратор видел разметку вместо причины.
 * Достаём сами сообщения, а при неожиданной форме показываем общий текст.
 */
export function readValidationMessage(raw: string | undefined): string {
  const fallback = 'Не удалось сохранить настройки';
  if (!raw) {
    return fallback;
  }

  const trimmed = raw.trim();
  if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) {
    return trimmed;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    const issues = Array.isArray(parsed) ? parsed : [parsed];
    const messages: string[] = [];
    for (const issue of issues) {
      if (typeof issue !== 'object' || issue === null || !('message' in issue)) {
        continue;
      }
      const message = Reflect.get(issue, 'message');
      if (typeof message === 'string' && message.length > 0) {
        messages.push(message);
      }
    }
    return messages.length > 0 ? messages.join('; ') : fallback;
  } catch {
    return trimmed;
  }
}

/** Читает ответ проверки ключа, не полагаясь на форму без проверки. */
function readProviderTestResult(payload: unknown): ProviderTestResult {
  if (typeof payload !== 'object' || payload === null) {
    return { ok: false };
  }
  const ok = 'ok' in payload && payload.ok === true;
  const error = 'error' in payload && typeof payload.error === 'string' ? payload.error : undefined;
  return { ok, error };
}

/** Известные значения статуса сессии перевода — держим в синхроне с enum вручную. */
const TRANSLATION_SESSION_STATUSES: readonly TranslationSessionStatus[] = [
  'STARTING',
  'LIVE',
  'DEGRADED',
  'STOPPING',
  'STOPPED',
  'FAILED',
];

function isTranslationSessionStatus(value: unknown): value is TranslationSessionStatus {
  return (
    typeof value === 'string' &&
    (TRANSLATION_SESSION_STATUSES as readonly string[]).includes(value)
  );
}

/** Читает ответ `GET .../translation/session`, не доверяя форме без проверки. */
function readSessionInfo(payload: unknown): TranslationSessionInfo | null {
  if (typeof payload !== 'object' || payload === null || !('status' in payload)) {
    return null;
  }
  const status = Reflect.get(payload, 'status');
  if (!isTranslationSessionStatus(status)) {
    return null;
  }
  const sessionRaw = 'session' in payload ? Reflect.get(payload, 'session') : null;
  const session = typeof sessionRaw === 'string' ? sessionRaw : null;
  return { status, session };
}

const VIEWER_MODE_LABELS: Record<TranslationViewerMode, string> = {
  ORIGINAL: 'Только оригинал',
  CAPTIONS: 'Оригинал + субтитры',
  DUB: 'Только озвучка',
  DUB_PLUS_CAPTIONS: 'Озвучка + субтитры',
  MIX: 'Обе дорожки (микс громкостей)',
};

const POLICY_LABELS: Record<TranslationSegmentPolicy, string> = {
  BALANCED: 'Сбалансированно — лекции',
  LOW_LATENCY: 'Быстрее — вопросы-ответы',
  HIGH_QUALITY: 'Точнее — доклады',
};

const VOICE_MODE_LABELS: Record<TranslationVoiceMode, string> = {
  SINGLE: 'Один голос на эфир',
  PER_SPEAKER: 'Свой голос каждому спикеру',
  NEUTRAL: 'Нейтральный голос',
};

const ORIGINAL_MIX_LABELS: Record<TranslationOriginalMix, string> = {
  MUTE: 'Заглушить оригинал',
  MIX: 'Слышны обе дорожки',
  DUCK_DB: 'Приглушать оригинал автоматически',
};

// Record по всем значениям enum — пропуск нового статуса поймает компилятор.
const SESSION_STATUS_LABELS: Record<TranslationSessionStatus, string> = {
  STARTING: 'Запускается',
  LIVE: 'Идёт',
  DEGRADED: 'Деградирован',
  STOPPING: 'Останавливается',
  STOPPED: 'Остановлен',
  FAILED: 'Ошибка',
};

/** Вариант Badge под статус — зелёный для рабочего состояния, тревожный для сбоя. */
const SESSION_STATUS_BADGE_VARIANT: Record<
  TranslationSessionStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  STARTING: 'secondary',
  LIVE: 'default',
  DEGRADED: 'destructive',
  STOPPING: 'secondary',
  STOPPED: 'outline',
  FAILED: 'destructive',
};

const NO_VOICE = 'none';

/** Sentinel «модель перевода по умолчанию» — бэкенд сам решит, какую взять. */
const MT_MODEL_DEFAULT = 'default';

/** Поставщик перевода — выводится из формы имени модели, отдельного поля в БД нет. */
type MtProvider = 'mistral' | 'openrouter';

const MT_PROVIDER_LABELS: Record<MtProvider, string> = {
  mistral: 'Mistral (прямой)',
  openrouter: 'OpenRouter (агрегатор)',
};

/** Select отдаёт произвольную строку — сужаем её проверкой, а не приведением типа. */
function isMtProvider(value: string): value is MtProvider {
  return value === 'mistral' || value === 'openrouter';
}

/**
 * Идёт ли модель напрямую в Mistral — дублирует соглашение
 * `isDirectMistralModel()` из `apps/backend/src/services/mistral.service.ts`:
 * имя без косой черты и с mistral-подобным префиксом — прямой вызов, имя
 * вида «вендор/модель» — через OpenRouter. Импортировать бэкендовый сервис в
 * админку нельзя, поэтому предикат продублирован здесь как чистая функция.
 */
const MISTRAL_MODEL_PREFIXES = [
  'mistral-',
  'open-mistral-',
  'open-mixtral-',
  'codestral-',
  'magistral-',
  'devstral',
  'pixtral-',
  'ministral-',
  'voxtral-',
] as const;

function isDirectMistralModel(modelId: string): boolean {
  const lower = modelId.toLowerCase();
  if (lower.includes('/')) {
    return false;
  }
  return MISTRAL_MODEL_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

/** Модель перевода по умолчанию для каждого провайдера при переключении. */
const MT_PROVIDER_DEFAULT_MODEL: Record<MtProvider, string> = {
  mistral: 'mistral-small-latest',
  // FALLBACK_MODEL из llm-mt.adapter.ts — единственная проверенная бесплатная.
  openrouter: 'google/gemma-4-26b-a4b-it:free',
};

interface MtModelOption {
  value: string;
  label: string;
}

/**
 * Модели Mistral (прямой вызов). `mistral-small-latest` — по умолчанию:
 * по замерам на живых фразах даёт корректный перевод при задержке ~0.6 с,
 * подходит для эфира.
 */
const MISTRAL_MT_MODELS: MtModelOption[] = [
  { value: 'mistral-small-latest', label: 'mistral-small-latest — быстрая (по умолчанию)' },
  { value: 'mistral-medium-latest', label: 'mistral-medium-latest' },
  { value: 'mistral-large-latest', label: 'mistral-large-latest' },
  { value: 'ministral-8b-latest', label: 'ministral-8b-latest' },
];

/** Модели через OpenRouter. Бесплатная — единственная проверенная, платные — популярные варианты. */
const OPENROUTER_MT_MODELS: MtModelOption[] = [
  { value: 'google/gemma-4-26b-a4b-it:free', label: 'google/gemma-4-26b-a4b-it:free — бесплатная' },
  { value: 'openai/gpt-4o-mini', label: 'openai/gpt-4o-mini' },
  { value: 'anthropic/claude-3.5-haiku', label: 'anthropic/claude-3.5-haiku' },
  { value: 'mistralai/mistral-small', label: 'mistralai/mistral-small' },
];

/**
 * Модели распознавания и синтеза. Значения обязаны совпадать с
 * `ELEVENLABS_ALLOWED_MODELS` в
 * `apps/backend/src/domains/livestream-translation/elevenlabs-credentials.service.ts`:
 * бэкенд принимает только их и молча откатывается на дефолт при незнакомом id.
 *
 * Пустое поле в конфиге означает «взять дефолт бэкенда» — в списке это пункт
 * «По умолчанию», а не отдельное значение в БД.
 */
// Список сверен с ответом провайдера живым запросом: других моделей
// распознавания у ElevenLabs нет. Прежний пункт `scribe_v2_realtime` убран —
// такой модели не существует, провайдер отвечает на неё ошибкой
// «is not a valid model_id», а бэкенд молча подменял её на scribe_v2.
const STT_MODELS: MtModelOption[] = [
  { value: 'scribe_v2', label: 'scribe_v2 — текущее поколение (по умолчанию)' },
  { value: 'scribe_v1', label: 'scribe_v1 — предыдущее поколение' },
  { value: 'scribe_v1_experimental', label: 'scribe_v1_experimental — экспериментальная' },
];

// Задержка в подписях — замер на проде (одна фраза, по три прогона на модель,
// 27.07.2026). Она напрямую прибавляется к отставанию озвучки от говорящего,
// поэтому вынесена в подпись: без неё «выразительнее» выглядит бесплатным.
const TTS_MODELS: MtModelOption[] = [
  { value: 'eleven_flash_v2_5', label: 'eleven_flash_v2_5 — ~0.4 с, минимальная задержка (по умолчанию)' },
  { value: 'eleven_turbo_v2_5', label: 'eleven_turbo_v2_5 — ~0.4 с' },
  { value: 'eleven_turbo_v2', label: 'eleven_turbo_v2' },
  { value: 'eleven_flash_v2', label: 'eleven_flash_v2' },
  { value: 'eleven_multilingual_v2', label: 'eleven_multilingual_v2 — ~1.2 с, качество' },
  { value: 'eleven_v3', label: 'eleven_v3 — ~2.6 с, выразительнее, но заметно медленнее' },
];

interface TranslationTabProps {
  livestreamId: number;
  canEdit: boolean;
}

export function TranslationTab({ livestreamId, canEdit }: TranslationTabProps) {
  const invalidate = useInvalidate();
  const configUrl = `${apiBase}/admin/livestreams/${livestreamId}/translation`;

  // useCustom сам разворачивает конверт ответа: в query.data.data лежит
  // полезная нагрузка, а не ещё один слой { data }.
  const { query } = useCustom<TranslationConfig>({
    url: configUrl,
    method: 'get',
  });

  const { query: voicesQuery } = useCustom<VoiceProfile[]>({
    url: `${apiBase}/admin/voice-profiles`,
    method: 'get',
  });

  // Статус сессии перевода — опрашиваем регулярно: конвейер живёт отдельно
  // от настроек и может оборваться (перезапуск бэкенда) без изменения
  // настроек, поэтому статус нельзя получить одним первичным запросом.
  const sessionUrl = `${apiBase}/admin/livestreams/${livestreamId}/translation/session`;
  const { query: sessionQuery } = useCustom<TranslationSessionInfo>({
    url: sessionUrl,
    method: 'get',
    queryOptions: {
      refetchInterval: 10_000,
    },
  });

  const config = query.data?.data;
  const sessionInfo = readSessionInfo(sessionQuery.data?.data);
  const voices = useMemo(
    () => (voicesQuery.data?.data ?? []).filter((voice) => !voice.archivedAt),
    [voicesQuery.data],
  );

  const [draft, setDraft] = useState<TranslationConfig | null>(null);
  useEffect(() => {
    if (config) {
      setDraft(config);
    }
  }, [config]);

  const { mutate: save, mutation: saveMutation } = useCustomMutation();
  const { mutate: testKey, mutation: testMutation } = useCustomMutation();
  const { mutate: startTranslation, mutation: startMutation } = useCustomMutation();
  const { mutate: stopTranslation, mutation: stopMutation } = useCustomMutation();

  // Язык оригинала остаётся в списке намеренно: выбранный как «язык
  // перевода», он даёт зрителю дорожку-расшифровку — субтитры на языке речи
  // без прогона через переводчик (см. translation-lane.ts).
  const languageOptions: MultiSelectOption[] = useMemo(
    () =>
      (config?.availableLanguages ?? []).map((code) => ({
        value: code,
        label: code.toUpperCase(),
      })),
    [config?.availableLanguages],
  );

  const patch = useCallback(
    <K extends keyof TranslationConfig>(key: K, value: TranslationConfig[K]) => {
      setDraft((current) => (current ? { ...current, [key]: value } : current));
    },
    [],
  );


  // Провайдер перевода — не отдельное поле формы, а производная от имени
  // текущей модели. Пустой mtModelId (режим «по умолчанию») трактуется как
  // Mistral, потому что бэкенд в этом случае подставляет DEFAULT_MODEL
  // ('mistral-small-latest', см. llm-mt.adapter.ts).
  const mtProvider: MtProvider =
    !draft?.mtModelId || isDirectMistralModel(draft.mtModelId) ? 'mistral' : 'openrouter';

  const handleMtProviderChange = useCallback((nextProvider: MtProvider) => {
    setDraft((current) =>
      current ? { ...current, mtModelId: MT_PROVIDER_DEFAULT_MODEL[nextProvider] } : current,
    );
  }, []);

  const handleSave = useCallback(() => {
    if (!draft) {
      return;
    }
    save(
      {
        url: configUrl,
        method: 'put',
        values: {
          enabled: draft.enabled,
          sourceLanguage: draft.sourceLanguage,
          targetLanguages: draft.targetLanguages,
          defaultViewerMode: draft.defaultViewerMode,
          defaultOriginalVolume: draft.defaultOriginalVolume,
          defaultTranslationVolume: draft.defaultTranslationVolume,
          policy: draft.policy,
          voiceMode: draft.voiceMode,
          defaultVoiceProfileId: draft.defaultVoiceProfileId,
          neutralVoiceProfileId: draft.neutralVoiceProfileId,
          originalUnderDub: draft.originalUnderDub,
          captionsEnabled: draft.captionsEnabled,
          dubEnabled: draft.dubEnabled,
          dualCaptionsEnabled: draft.dualCaptionsEnabled,
          startPolicy: draft.startPolicy,
          maxConcurrentLanes: draft.maxConcurrentLanes,
          diarizationEnabled: draft.diarizationEnabled,
          mtModelId: draft.mtModelId,
          sttModelId: draft.sttModelId,
          ttsModelId: draft.ttsModelId,
        },
      },
      {
        onSuccess: () => {
          notify.success('Настройки перевода сохранены');
          invalidate({ resource: 'livestreams', invalidates: ['detail'], id: livestreamId });
          void query.refetch();
        },
        onError: (error) => {
          notify.error(readValidationMessage(error?.message));
        },
      },
    );
  }, [draft, configUrl, save, invalidate, livestreamId, query]);

  /**
   * Ручной запуск конвейера перевода на уже идущем эфире.
   *
   * Обычно перевод поднимается сам с началом эфира. Ручка нужна, когда
   * конвейер оборвался посреди трансляции — например, при перезапуске
   * сервера: запись сессии остаётся в базе, а распознавание уже не идёт,
   * и дождаться восстановления без перезапуска эфира невозможно.
   */
  const handleStartTranslation = useCallback(() => {
    startTranslation(
      {
        url: `${apiBase}/admin/livestreams/${livestreamId}/translation/start`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Перевод запущен');
          void sessionQuery.refetch();
        },
        onError: (error) => {
          notify.error(readValidationMessage(error?.message));
        },
      },
    );
  }, [startTranslation, livestreamId, sessionQuery]);

  const handleStopTranslation = useCallback(() => {
    stopTranslation(
      {
        url: `${apiBase}/admin/livestreams/${livestreamId}/translation/stop`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Перевод остановлен');
          void sessionQuery.refetch();
        },
        onError: (error) => {
          notify.error(readValidationMessage(error?.message));
        },
      },
    );
  }, [stopTranslation, livestreamId, sessionQuery]);

  const handleTestKey = useCallback(() => {
    testKey(
      {
        url: `${apiBase}/admin/integrations/elevenlabs/test`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: (response) => {
          const result = readProviderTestResult(response?.data);
          if (result.ok) {
            notify.success('Ключ ElevenLabs принят провайдером');
          } else {
            notify.error(result.error ?? 'Провайдер отклонил ключ');
          }
        },
        onError: (error) => notify.error(error?.message ?? 'Проверка не удалась'),
      },
    );
  }, [testKey]);


  if (query.isLoading && !draft) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Загрузка настроек перевода…
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Настройки перевода недоступны
      </div>
    );
  }

  const disabled = !canEdit || saveMutation.isPending;
  // Режим «свой голос каждому» требует нейтрального голоса и включённого
  // разделения по говорящим — иначе речь, которую не удалось приписать
  // конкретному человеку, будет нечем озвучить.
  const perSpeakerBlocked = !draft.neutralVoiceProfileId || !draft.diarizationEnabled;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              Перевод в реальном времени
            </CardTitle>
            <CardDescription>
              Речь эфира распознаётся, переводится и озвучивается для зрителей на выбранных языках.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {draft.providerConfigured ? (
              <Badge variant="outline" className="gap-1">
                <Check className="h-3 w-3" /> ElevenLabs подключён
              </Badge>
            ) : (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" /> Нет ключа ElevenLabs
              </Badge>
            )}
            <Switch
              checked={draft.enabled}
              onCheckedChange={(checked) => patch('enabled', checked)}
              disabled={disabled}
            />
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Состояние перевода</span>
                {sessionInfo ? (
                  <Badge variant={SESSION_STATUS_BADGE_VARIANT[sessionInfo.status]}>
                    {SESSION_STATUS_LABELS[sessionInfo.status]}
                  </Badge>
                ) : (
                  <Badge variant="outline">Нет данных</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Перевод поднимается автоматически с началом эфира. Кнопка «Запустить» нужна, если
                перевод включили уже во время идущего эфира или конвейер оборвался — например,
                после перезапуска сервера.
              </p>
              {!draft.enabled && (
                <p className="text-xs text-muted-foreground">
                  Сначала включите перевод переключателем выше и сохраните настройки.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartTranslation}
                disabled={disabled || !draft.enabled || startMutation.isPending}
              >
                Запустить перевод
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleStopTranslation}
                disabled={disabled || stopMutation.isPending}
              >
                Остановить
              </Button>
            </div>
          </div>

          {!draft.providerConfigured && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              Ключ ElevenLabs не сохранён — перевод не запустится. Добавьте его в разделе
              «Интеграции», затем проверьте связь.
              <Button
                variant="outline"
                size="sm"
                className="ml-3"
                onClick={handleTestKey}
                disabled={testMutation.isPending}
              >
                Проверить ключ
              </Button>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Язык речи на эфире</Label><FieldHint text="На каком языке говорят в эфире. От него отталкивается распознавание речи, и он же становится языком дорожки-расшифровки. Ошибка здесь ломает всё: речь будет распознана неверно, а значит и переведена неверно." /></span>
              <Select
                value={draft.sourceLanguage}
                onValueChange={(value) => patch('sourceLanguage', value)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(config?.availableLanguages ?? []).map((code) => (
                    <SelectItem key={code} value={code}>
                      {code.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Языки перевода</Label><FieldHint text="На какие языки переводить речь. Каждый язык — отдельная дорожка субтитров и озвучки, которую зритель выбирает в плеере. Чем больше языков, тем больше запросов к моделям и выше расход." /></span>
              <MultiSelect
                options={languageOptions}
                value={draft.targetLanguages}
                onChange={(value) => patch('targetLanguages', value)}
                placeholder="Выберите языки"
                disabled={disabled}
              />
              <p className="text-xs text-muted-foreground">
                Не путать с «языками трансляции» на вкладке доступа: там список решает, кто видит
                эфир, здесь — на какие языки его переводить.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Провайдер перевода</Label><FieldHint text="Через кого идут запросы на перевод. Прямой Mistral отвечает быстрее агрегатора, а задержка перевода напрямую добавляется к отставанию от говорящего." /></span>
              <Select
                value={mtProvider}
                onValueChange={(value) => {
                  if (isMtProvider(value)) {
                    handleMtProviderChange(value);
                  }
                }}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MT_PROVIDER_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Модель перевода</Label><FieldHint text="Какая модель переводит распознанный текст. Более крупная переводит точнее, но отвечает дольше — и зритель дольше ждёт субтитр и озвучку." /></span>
              <Select
                value={draft.mtModelId ?? MT_MODEL_DEFAULT}
                onValueChange={(value) =>
                  patch('mtModelId', value === MT_MODEL_DEFAULT ? null : value)
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={MT_MODEL_DEFAULT}>По умолчанию</SelectItem>
                  {(mtProvider === 'mistral' ? MISTRAL_MT_MODELS : OPENROUTER_MT_MODELS).map(
                    (option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Прямой Mistral предпочтителен на эфире: задержка ответа модели напрямую
                добавляется к отставанию перевода от говорящего, а замер на живых фразах даёт
                ~0.6 с через Mistral против ~1.9 с через OpenRouter.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-center justify-between rounded-md border p-3">
              <span className="flex items-center gap-1.5"><Label className="cursor-pointer">Субтитры</Label><FieldHint text="Показывать ли зрителю текст перевода. Выключение убирает субтитры и дорожку-расшифровку оригинала, то есть и двойные субтитры в плеере." /></span>
              <Switch
                checked={draft.captionsEnabled}
                onCheckedChange={(checked) => patch('captionsEnabled', checked)}
                disabled={disabled}
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <span className="flex items-center gap-1.5"><Label className="cursor-pointer">Озвучка</Label><FieldHint text="Синтезировать ли голос перевода. Выключение оставляет только субтитры и убирает расход на синтез речи. Язык самого эфира не озвучивается никогда — на нём зритель слышит живую речь." /></span>
              <Switch
                checked={draft.dubEnabled}
                onCheckedChange={(checked) => patch('dubEnabled', checked)}
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Запуск</Label><FieldHint text="Когда включается перевод. Автоматически — с началом вещания; вручную — кнопкой на этой вкладке. Перевод живёт внутри процесса и после перезапуска сервера его нужно запускать заново." /></span>
              <Select
                value={draft.startPolicy}
                onValueChange={(value) => patch('startPolicy', value as TranslationStartPolicy)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ON_LIVE">Автоматически с началом эфира</SelectItem>
                  <SelectItem value="MANUAL">Вручную</SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mic className="h-5 w-5" />
            Голос озвучки
          </CardTitle>
          <CardDescription>
            Модель не узнаёт человека по имени — какой голос кому принадлежит, задаёте вы.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Как выбирать голос</Label><FieldHint text="Каким голосом звучит перевод: одним на весь эфир или своим для каждого говорящего. Второй режим требует включённого разделения по говорящим и заданного нейтрального голоса." /></span>
              <Select
                value={draft.voiceMode}
                onValueChange={(value) => patch('voiceMode', value as TranslationVoiceMode)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VOICE_MODE_LABELS).map(([value, label]) => (
                    <SelectItem
                      key={value}
                      value={value}
                      disabled={value === 'PER_SPEAKER' && perSpeakerBlocked}
                    >
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {perSpeakerBlocked && (
                <p className="text-xs text-muted-foreground">
                  Режим «свой голос каждому» станет доступен, когда выбран нейтральный голос и
                  включено разделение по говорящим.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Голос спикера</Label><FieldHint text="Голос, которым озвучивается закреплённый за ним человек. Применяется только в режиме «свой голос каждому»." /></span>
              <Select
                value={draft.defaultVoiceProfileId ? String(draft.defaultVoiceProfileId) : NO_VOICE}
                onValueChange={(value) =>
                  patch('defaultVoiceProfileId', value === NO_VOICE ? null : Number(value))
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Не выбран" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_VOICE}>Не выбран</SelectItem>
                  {voices.map((voice) => (
                    <SelectItem key={voice.id} value={String(voice.id)}>
                      {voice.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Нейтральный голос</Label><FieldHint text="Запасной голос: им озвучивается речь, которую не удалось приписать конкретному человеку. Без него такая речь останется без озвучки." /></span>
              <Select
                value={draft.neutralVoiceProfileId ? String(draft.neutralVoiceProfileId) : NO_VOICE}
                onValueChange={(value) =>
                  patch('neutralVoiceProfileId', value === NO_VOICE ? null : Number(value))
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Не выбран" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_VOICE}>Не выбран</SelectItem>
                  {voices.map((voice) => (
                    <SelectItem key={voice.id} value={String(voice.id)}>
                      {voice.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Им озвучивается речь, которую не удалось уверенно приписать конкретному спикеру.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <span className="flex items-center gap-1.5"><Label className="cursor-pointer">Разделять говорящих</Label><FieldHint text="Помечать в распознавании, кто из говорящих произнёс фразу. Нужно, чтобы разным людям давать разные голоса; немного повышает задержку распознавания." /></span>
                <p className="text-xs text-muted-foreground">
                  Требует подтверждённой поддержки в потоковом режиме.
                </p>
              </div>
              <Switch
                checked={draft.diarizationEnabled}
                onCheckedChange={(checked) => patch('diarizationEnabled', checked)}
                disabled={disabled}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Что слышит и видит зритель
          </CardTitle>
          <CardDescription>
            Настройки по умолчанию — зритель может изменить их в плеере.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Режим по умолчанию</Label><FieldHint text="Что получит зритель, впервые открывший эфир: только субтитры, только озвучку или и то и другое. Дальше он меняет это в плеере под себя." /></span>
              <Select
                value={draft.defaultViewerMode}
                onValueChange={(value) => patch('defaultViewerMode', value as TranslationViewerMode)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VIEWER_MODE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Модель распознавания речи</Label><FieldHint text="Какая модель переводит звук в текст. От неё зависит качество всей цепочки: ошибку распознавания переводчик добросовестно переведёт и озвучит." /></span>
              <Select
                value={draft.sttModelId ?? MT_MODEL_DEFAULT}
                onValueChange={(value) =>
                  patch('sttModelId', value === MT_MODEL_DEFAULT ? null : value)
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={MT_MODEL_DEFAULT}>По умолчанию</SelectItem>
                  {STT_MODELS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Модель озвучки</Label><FieldHint text="Какая модель синтезирует голос перевода. Её задержка целиком прибавляется к отставанию озвучки от говорящего: по замеру на проде flash и turbo отвечают за ~0.4 с, multilingual_v2 за ~1.2 с, а eleven_v3 за ~2.6 с — в шесть раз медленнее." /></span>
              <Select
                value={draft.ttsModelId ?? MT_MODEL_DEFAULT}
                onValueChange={(value) =>
                  patch('ttsModelId', value === MT_MODEL_DEFAULT ? null : value)
                }
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={MT_MODEL_DEFAULT}>По умолчанию</SelectItem>
                  {TTS_MODELS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Оригинал под озвучкой</Label><FieldHint text="Что делать с живым звуком эфира, пока звучит перевод: приглушить или заглушить полностью. Влияет только на зрителей с включённой озвучкой." /></span>
              <Select
                value={draft.originalUnderDub}
                onValueChange={(value) => patch('originalUnderDub', value as TranslationOriginalMix)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ORIGINAL_MIX_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <span className="flex items-center gap-1.5">
                <Label>Громкость оригинала: {draft.defaultOriginalVolume}%</Label>
                <FieldHint text="Насколько громко звучит живой звук эфира у зрителя, впервые открывшего трансляцию. Дальше он регулирует громкость сам в плеере." />
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={draft.defaultOriginalVolume}
                onChange={(event) => patch('defaultOriginalVolume', Number(event.target.value))}
                disabled={disabled}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <span className="flex items-center gap-1.5">
                <Label>Громкость перевода: {draft.defaultTranslationVolume}%</Label>
                <FieldHint text="Насколько громко звучит озвучка перевода при первом открытии эфира. Ноль означает, что зритель по умолчанию слышит только оригинал, а перевод читает субтитрами." />
              </span>
              <input
                type="range"
                min={0}
                max={100}
                value={draft.defaultTranslationVolume}
                onChange={(event) => patch('defaultTranslationVolume', Number(event.target.value))}
                disabled={disabled}
                className="w-full"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Как нарезать речь</Label><FieldHint text="Чем длиннее куски, на которые режется речь, тем связнее перевод, но тем дольше зритель ждёт фразу. Короткие куски дают быстрый, но более рваный перевод." /></span>
              <Select
                value={draft.policy}
                onValueChange={(value) => patch('policy', value as TranslationSegmentPolicy)}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(POLICY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Озвучка начинается только после законченной фразы, поэтому отстаёт от речи на
                несколько секунд.
              </p>
            </div>

            <div className="space-y-2">
              <span className="flex items-center gap-1.5"><Label>Максимум языков одновременно</Label><FieldHint text="Сколько дорожек перевода эфир поднимает разом. Ограничивает нагрузку и расход: языки сверх лимита переводиться не будут." /></span>
              <Select
                value={String(draft.maxConcurrentLanes)}
                onValueChange={(value) => patch('maxConcurrentLanes', Number(value))}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 8, 10].map((count) => (
                    <SelectItem key={count} value={String(count)}>
                      {count}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <VoiceLibraryPanel canEdit={canEdit} />

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={disabled} className="gap-2">
          <Save className="h-4 w-4" />
          Сохранить настройки перевода
        </Button>
      </div>
    </div>
  );
}
